import { getUserCartDetails } from '../../util/dataExtractors';
import { isArrayLength } from '../../util/genericHelpers';
import { getLocalCartItems } from '../../util/guestStorage';
import { getSellerVacation } from '../../util/vacation';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  cartQuantityOf,
  cartUnitCount,
  isCartListingPurchasable,
  isOwnCartListing,
} from '../../util/cartRules';

const { Money } = sdkTypes;

export const extractListingDataForCart = (listing, selectedQuantity = 1) => {
  const currentStock = listing && listing.currentStock;
  const quantity = currentStock?.attributes?.quantity;

  const price = listing?.attributes?.price;
  const currency = price?.currency;

  const listingId = listing?.id?.uuid;
  const title = listing?.attributes?.title;
  const authorId = listing?.author?.id?.uuid;
  const publicData = listing?.attributes?.publicData;
  const shippingPriceInSubunitsOneItem = publicData?.shippingPriceInSubunitsOneItem;
  const shippingPriceInSubunitsAdditionalItems = publicData?.shippingPriceInSubunitsAdditionalItems;
  const firstImage =
    listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url ??
    listing?.images?.[0]?.attributes?.variants?.['default']?.url;

  // Extract handling costs for aggregation
  const domesticHandlingCost = publicData?.domesticParams?.domesticHandlingCost || 0;
  const internationalHandlingCost = publicData?.internationalParams?.internationalHandlingCost || 0;

  // Extract category info for tax calculation
  const categoryLevel1 = publicData?.categoryLevel1;
  const categoryLevel2 = publicData?.categoryLevel2;
  const categoryLevel3 = publicData?.categoryLevel3;
  const categoryLevel4 = publicData?.categoryLevel4;

  return {
    listingId,
    stockCount: selectedQuantity,
    price,
    title,
    authorId,
    shippingPriceInSubunitsOneItem,
    shippingPriceInSubunitsAdditionalItems,
    currency,
    oldStock: quantity,
    firstImage,
    domesticHandlingCost,
    internationalHandlingCost,
    categoryLevel1,
    categoryLevel2,
    categoryLevel3,
    categoryLevel4,
    listing, // Include full listing for access to all data
  };
};

// Guest cart storage lives in util/guestStorage so ducks outside CartPage
// (e.g. user.duck's cart count) can read it without an import cycle.
export {
  getLocalCartItems,
  setLocalCartItems,
  removeLocalCartItems,
} from '../../util/guestStorage';

// The profile's cart and this browser's guest cart together, each listing once.
export const getAllCartItems = currentUser =>
  Array.from(new Set([...(getUserCartDetails(currentUser) || []), ...(getLocalCartItems() || [])]));

/**
 * @param {boolean} params.isAuthenticated - Whether the user is authenticated.
 * @returns {boolean|null} True if the listing is in the cart, false if not, or null if params are invalid.
 */
export const isItemAddedToCart = ({ cartItems = [], listingId, isAuthenticated } = {}) => {
  // Return null for invalid or empty params
  if (!listingId || typeof isAuthenticated !== 'boolean') {
    return null;
  }

  // Helper to check if array is non-empty and includes listingId
  const isInCart = items => isArrayLength(items) && items.includes(listingId);

  if (isAuthenticated) {
    return isInCart(cartItems);
  }

  const localCartItems = getLocalCartItems();
  return isInCart(localCartItems);
};

/**
 * The total of some cart items (from extractListingDataForCart): the price of each
 * times its quantity, as Money, and the number of units. The price is null when there
 * is nothing to add up or the items are priced in different currencies.
 */
export const getTotal = cartItems => {
  const priced = (cartItems || []).filter(item => item?.price);
  const items = priced.reduce((sum, item) => sum + item.stockCount, 0);
  const currency = priced[0]?.price?.currency;
  if (!currency || priced.some(item => item.price.currency !== currency)) {
    return { price: null, items };
  }
  const subunits = priced.reduce((sum, item) => sum + item.price.amount * item.stockCount, 0);
  return { price: new Money(subunits, currency), items };
};

/**
 * The cart as the page shows it: one entry per shop, in cart order, each with its rows.
 * The buyer's own listings are left out. A row that can't be bought (sold out or closed)
 * stays on the page but isn't in the shop's cartItems or totals.
 *
 * shop: { id, author, listings, rows, cartItems, units, subtotal }
 * row: { id, listing, quantity, purchasable }
 */
export const cartShopsOf = (listings, quantities, currentUserId) => {
  const shops = [];
  const byAuthor = {};
  (listings || []).forEach(listing => {
    if (!listing?.id || isOwnCartListing(listing, currentUserId)) return;
    const authorId = listing.author?.id?.uuid || listing.id.uuid;
    if (!byAuthor[authorId]) {
      byAuthor[authorId] = { id: authorId, author: listing.author, listings: [], rows: [] };
      shops.push(byAuthor[authorId]);
    }
    const quantity = cartQuantityOf(listing, quantities);
    const purchasable = isCartListingPurchasable(listing);
    byAuthor[authorId].listings.push(listing);
    byAuthor[authorId].rows.push({ id: listing.id.uuid, listing, quantity, purchasable });
  });
  return shops.map(shop => {
    const cartItems = shop.rows
      .filter(row => row.purchasable)
      .map(row => extractListingDataForCart(row.listing, row.quantity));
    const { price, items } = getTotal(cartItems);
    return { ...shop, cartItems, units: items, subtotal: price };
  });
};

/**
 * The whole cart: its units (the same count as the topbar's), the shops that have
 * something to buy, and the total of all of them.
 */
export const cartSummaryOf = (shops, listings, quantities, currentUserId) => {
  const buyable = (shops || []).filter(shop => shop.units > 0);
  const { price } = getTotal(buyable.flatMap(shop => shop.cartItems));
  return {
    units: cartUnitCount(listings, quantities, currentUserId),
    shops: buyable,
    total: price,
  };
};

/**
 * Validates if all cart items support international shipping when buyer address is international
 * Note: Pickup/shipping availability validation is no longer needed here since we hide
 * delivery methods that aren't supported by all items in the cart
 * @param {Array} cartItems - Array of cart items with listing data
 * @param {boolean} isDomestic - Whether shipping is domestic
 * @returns {Object} - { isValid: boolean, incompatibleItems: Array }
 */
export const validateInternationalShipping = (cartItems, isDomestic) => {
  // Only validate for international orders
  if (isDomestic || !isArrayLength(cartItems)) {
    return { isValid: true, incompatibleItems: [] };
  }

  const incompatibleItems = [];

  cartItems.forEach(item => {
    const listing = item.listing;
    if (!listing) return;

    const publicData = listing.attributes?.publicData || {};
    const internationalParams = publicData.internationalParams || {};

    const isInternationalDisabled = Array.isArray(
      internationalParams.isInternationalShippingDisabled
    )
      ? internationalParams.isInternationalShippingDisabled.length > 0
      : Boolean(internationalParams.isInternationalShippingDisabled);

    if (isInternationalDisabled) {
      incompatibleItems.push({
        title: item.title,
        listingId: item.listingId,
        reason: 'international-shipping-disabled',
      });
    }
  });

  return {
    isValid: incompatibleItems.length === 0,
    incompatibleItems,
  };
};

/**
 * The vacation of the seller of one cart group (all its listings are theirs),
 * or null when they are not away. Their items can't be checked out until
 * they are back; the server refuses it too.
 */
export const sellerVacationOfGroup = (groupedListings, now = new Date()) =>
  getSellerVacation(groupedListings?.[0]?.author, now);
